import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Dumbbell, Apple, Droplet, Pill, Lightbulb, Lock, Sparkles, CheckCircle, MessageCircle, Activity } from "lucide-react";
import { toast } from "sonner";

export default function Plan() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const paidSession = params.get("paid_session");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [confirming, setConfirming] = useState(Boolean(paidSession));
  const attemptsRef = useRef(0);

  const loadPlan = async () => {
    const { data } = await api.get(`/plans/${id}`);
    setPlan(data);
    return data;
  };

  // Initial load
  useEffect(() => {
    loadPlan().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Post-payment polling: when ?paid_session=... is present, poll status until unlock applies
  useEffect(() => {
    if (!paidSession) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 15) {
        setConfirming(false);
        toast.error("Payment is taking longer than usual. Refreshing in a moment.");
        // Reload after a short delay in case the webhook eventually fires
        setTimeout(() => loadPlan(), 3000);
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data: st } = await api.get(`/checkout/status/${paidSession}`);
        if (st.payment_status === "paid") {
          const fresh = await loadPlan();
          if (fresh?.unlocked) {
            toast.success("Plan unlocked. Let's get to work.");
            setConfirming(false);
            const next = new URLSearchParams(params);
            next.delete("paid_session");
            setParams(next, { replace: true });
            return;
          }
        }
      } catch {
        // continue
      }
      setTimeout(poll, 1500);
    };
    // Start IMMEDIATELY (no initial delay) so users don't see a stale locked view
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidSession]);

  const unlock = async () => {
    setUnlocking(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: [{ product_id: "ai-plan", quantity: 1, plan_id: id }],
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="plan-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF3333]" />
      </div>
    );
  }
  if (!plan) return <div className="text-center py-32 font-mono text-white/40">Plan not found.</div>;

  // -------- POST-PAYMENT FULL-SCREEN CONFIRMATION (during the brief webhook wait) --------
  if (confirming && !plan.unlocked) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6" data-testid="plan-confirming-full">
        <div className="max-w-xl w-full text-center">
          <div className="relative inline-block">
            <div className="absolute inset-0 rounded-full bg-[#00E5FF]/20 blur-2xl animate-pulse" />
            <div className="relative w-20 h-20 rounded-full border-2 border-[#00E5FF]/60 flex items-center justify-center bg-black mx-auto">
              <Loader2 className="w-9 h-9 animate-spin text-[#00E5FF]" />
            </div>
          </div>
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#00E5FF] mt-8">
            CONFIRMING PAYMENT
          </div>
          <h1 className="font-heading uppercase text-4xl md:text-5xl mt-3 leading-tight">
            Forging your plan...
          </h1>
          <p className="font-sub text-white/50 mt-4">
            Stripe is confirming the charge. This usually takes 3-10 seconds.
            <br />Do not close this tab.
          </p>
          <div className="mt-8 font-jp text-6xl text-white/10 select-none">力</div>
        </div>
      </div>
    );
  }

  // -------- LOCKED VIEW --------
  if (!plan.unlocked) {
    const preview = plan.preview || {};
    const q = plan.questionnaire || {};
    return (
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-12" data-testid="plan-locked">
        {confirming && (
          <div className="brutal-border-blue bg-[#00E5FF]/5 px-4 py-3 mb-6 flex items-center gap-3" data-testid="plan-confirming">
            <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" />
            <div className="font-mono text-xs tracking-widest text-[#00E5FF]">CONFIRMING PAYMENT — UNLOCKING YOUR PLAN</div>
          </div>
        )}

        {/* Header */}
        <div className="relative brutal-border bg-[#0a0a0a] p-8 md:p-12 mb-8 overflow-hidden">
          <div className="absolute top-4 right-6 font-jp text-7xl text-white/5 select-none">力</div>
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#00E5FF]">FORGE-AI · PERSONAL PROTOCOL · LOCKED</div>
          <h1 className="font-heading uppercase text-4xl md:text-6xl leading-[0.95] mt-3 grunge-text" data-testid="plan-mantra">
            {preview.mantra || "Your Forged Protocol Is Ready"}
          </h1>
          <p className="font-sub text-white/60 mt-4 max-w-3xl text-lg" data-testid="plan-summary">
            {preview.summary}
          </p>
        </div>

        {/* Paywall */}
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 brutal-border-red bg-gradient-to-br from-[#FF3333]/10 via-[#0a0a0a] to-[#0a0a0a] p-8 relative overflow-hidden">
            <Lock className="absolute top-6 right-6 w-12 h-12 text-[#FF3333]/30" />
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#FF3333]">UNLOCK · ONE-TIME PAYMENT</div>
            <div className="font-heading text-7xl md:text-8xl mt-3" data-testid="plan-price">
              <span className="text-stroke-red">$</span>
              <span>{Math.floor(plan.price ?? 19)}</span>
            </div>
            <div className="font-sub text-white/60 text-lg mt-4 max-w-md">
              Pay once. Your full personalized protocol — workouts, macros, meals, supplements — unlocks instantly and stays saved to your account forever.
            </div>

            <button
              onClick={unlock}
              disabled={unlocking || confirming}
              data-testid="plan-unlock-btn"
              className="btn-forged-red mt-8 disabled:opacity-50"
            >
              {unlocking ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to checkout...</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Unlock Full Plan</>
              )}
            </button>
            <div className="font-mono text-[9px] tracking-widest text-white/30 mt-4">SECURE STRIPE CHECKOUT</div>
            <div className="font-mono text-[9px] tracking-widest text-[#FFB800]/70 mt-2">
              TEST MODE — USE CARD 4242 4242 4242 4242 · ANY FUTURE DATE · ANY CVC
            </div>
          </div>

          <div className="lg:col-span-5 space-y-3">
            <div className="font-mono text-[10px] tracking-[0.4em] text-white/40">WHAT YOU GET</div>
            {[
              { icon: Dumbbell, label: `${preview.training_days || q.days_per_week || 4}-day personalized split`, color: "#FF3333" },
              { icon: Apple, label: `${preview.daily_calories || "Calorie"}-targeted nutrition`, color: "#00E5FF" },
              { icon: Apple, label: `${preview.meal_count || "Full"} meal-by-meal breakdown`, color: "#00E5FF" },
              { icon: Pill, label: "Supplement protocol", color: "#FFB800" },
              { icon: Droplet, label: "Hydration & recovery targets", color: "#00E5FF" },
              { icon: Lightbulb, label: "Weekly tips from the forge", color: "#FFB800" },
            ].map((f) => (
              <div key={f.label} className="brutal-border bg-[#0a0a0a] p-4 flex items-center gap-3">
                <f.icon className="w-5 h-5 flex-shrink-0" style={{ color: f.color }} />
                <div className="font-sub text-white/80 text-sm">{f.label}</div>
                <CheckCircle className="w-4 h-4 text-white/30 ml-auto" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // -------- UNLOCKED VIEW (full plan) --------
  const p = plan.plan || {};
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="plan-page">
      {/* Quick-action bar */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Link to={`/plan/${id}/chat`} className="btn-forged-red" data-testid="plan-chat-link">
          <MessageCircle className="w-4 h-4" /> Chat with FORGE-AI
        </Link>
        <Link to="/track/nutrition" className="btn-forged-blue" data-testid="plan-nutrition-link">
          <Apple className="w-4 h-4" /> Log Macros
        </Link>
        <Link to="/track/workouts" className="btn-forged-ghost" data-testid="plan-workouts-link">
          <Activity className="w-4 h-4" /> Log Lifts
        </Link>
      </div>

      {/* Header */}
      <div className="relative brutal-border bg-[#0a0a0a] p-8 md:p-12 mb-10 overflow-hidden">
        <div className="absolute top-4 right-6 font-jp text-7xl text-white/5 select-none">力</div>
        <div className="font-mono text-[10px] tracking-[0.4em] text-[#00E5FF]">FORGE-AI · PERSONAL PROTOCOL</div>
        <h1 className="font-heading uppercase text-4xl md:text-6xl leading-[0.95] mt-3 grunge-text" data-testid="plan-mantra">
          {p.mantra || "Forge The Body. Master The Mind."}
        </h1>
        <p className="font-sub text-white/60 mt-4 max-w-3xl text-lg" data-testid="plan-summary">
          {p.summary}
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Workouts */}
        <div className="lg:col-span-8">
          <div className="flex items-center gap-3 mb-6">
            <Dumbbell className="w-5 h-5 text-[#FF3333]" />
            <h2 className="font-heading uppercase text-2xl tracking-wide">Weekly Workouts</h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <div className="space-y-4">
            {(p.weekly_workouts || []).map((w, i) => (
              <div key={`${w.day}-${i}`} className="brutal-border bg-[#0a0a0a] p-6" data-testid={`workout-${i}`}>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <div className="font-heading uppercase text-2xl">{w.day}</div>
                    <div className="font-mono text-[10px] tracking-widest text-[#FF3333] uppercase">{w.focus}</div>
                  </div>
                  <div className="font-jp text-3xl text-white/10">{["月","火","水","木","金","土","日"][i] || ""}</div>
                </div>
                <div className="space-y-2">
                  {(w.exercises || []).map((ex, j) => (
                    <div key={`${ex.name}-${j}`} className="grid grid-cols-12 gap-3 py-2 border-t border-white/5 font-sub text-sm">
                      <div className="col-span-6 md:col-span-5 text-white">{ex.name}</div>
                      <div className="col-span-2 font-mono text-xs text-white/60">{ex.sets}×{ex.reps}</div>
                      <div className="col-span-12 md:col-span-5 font-mono text-xs text-white/40 mt-1 md:mt-0">{ex.notes || ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Diet */}
        <div className="lg:col-span-4 space-y-6">
          <div className="brutal-border bg-[#0a0a0a] p-6">
            <div className="flex items-center gap-3 mb-4">
              <Apple className="w-5 h-5 text-[#00E5FF]" />
              <h3 className="font-heading uppercase text-xl">Macros</h3>
            </div>
            <div className="font-heading text-5xl text-[#00E5FF]" data-testid="plan-calories">
              {p.diet?.daily_calories || "—"}
            </div>
            <div className="font-mono text-[10px] text-white/40 tracking-widest mb-6">KCAL / DAY</div>
            <div className="space-y-3">
              {[
                ["Protein", p.diet?.macros?.protein_g, "#FF3333"],
                ["Carbs", p.diet?.macros?.carbs_g, "#00E5FF"],
                ["Fat", p.diet?.macros?.fat_g, "#FFB800"],
              ].map(([label, val, color]) => (
                <div key={label}>
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-white/60">{label}</span>
                    <span className="text-white">{val ?? "—"}g</span>
                  </div>
                  <div className="h-1 bg-white/5 mt-1">
                    <div className="h-full" style={{ width: `${Math.min(100, ((val || 0) / 250) * 100)}%`, background: color, boxShadow: `0 0 8px ${color}` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {p.diet?.hydration_l != null && (
            <div className="brutal-border bg-[#0a0a0a] p-6">
              <div className="flex items-center gap-3 mb-3">
                <Droplet className="w-5 h-5 text-[#00E5FF]" />
                <h3 className="font-heading uppercase text-xl">Hydration</h3>
              </div>
              <div className="font-heading text-4xl text-white">{p.diet.hydration_l}<span className="text-lg text-white/40 ml-2">L/day</span></div>
            </div>
          )}

          {p.diet?.supplements?.length > 0 && (
            <div className="brutal-border bg-[#0a0a0a] p-6">
              <div className="flex items-center gap-3 mb-3">
                <Pill className="w-5 h-5 text-[#FF3333]" />
                <h3 className="font-heading uppercase text-xl">Supplements</h3>
              </div>
              <ul className="space-y-1 font-sub text-sm text-white/70">
                {p.diet.supplements.map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Meals */}
        {p.diet?.meals?.length > 0 && (
          <div className="lg:col-span-12">
            <div className="flex items-center gap-3 mb-6">
              <Apple className="w-5 h-5 text-[#00E5FF]" />
              <h2 className="font-heading uppercase text-2xl tracking-wide">Daily Meals</h2>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {p.diet.meals.map((m, i) => (
                <div key={`${m.meal}-${i}`} className="brutal-border bg-[#0a0a0a] p-5" data-testid={`meal-${i}`}>
                  <div className="font-heading uppercase text-lg">{m.meal}</div>
                  <div className="font-mono text-[10px] text-[#FF3333] tracking-widest">{m.calories} KCAL</div>
                  <ul className="mt-3 space-y-1 font-sub text-sm text-white/70">
                    {(m.items || []).map((it, j) => <li key={`${it}-${j}`}>· {it}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tips */}
        {p.weekly_tips?.length > 0 && (
          <div className="lg:col-span-12 brutal-border bg-[#0a0a0a] p-6">
            <div className="flex items-center gap-3 mb-4">
              <Lightbulb className="w-5 h-5 text-[#FFB800]" />
              <h3 className="font-heading uppercase text-xl">Tips From The Forge</h3>
            </div>
            <ul className="grid md:grid-cols-2 gap-3 font-sub text-white/70">
              {p.weekly_tips.map((t) => (
                <li key={t} className="border-l-2 border-[#FFB800] pl-4">{t}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Plan-level disclaimer */}
        <div className="lg:col-span-12 brutal-border bg-black/40 p-5" data-testid="plan-disclaimer">
          <div className="font-mono text-[9px] tracking-[0.3em] text-white/40 mb-2">DISCLAIMER · EDUCATIONAL USE ONLY</div>
          <p className="font-sub text-white/50 text-xs leading-relaxed">
            This plan is AI-generated for educational and informational purposes only. It is NOT medical or nutritional advice and is not a substitute for consultation with a licensed physician, registered dietitian, or qualified fitness professional. You participate at your own risk and assume full responsibility for any outcome. Forged Athletics, its creators, affiliates, and licensors disclaim all liability for any injury, illness, or damages.{" "}
            <Link to="/legal" className="text-[#FF3333] hover:underline">Read the full disclaimer →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
